import { Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';

import { VehicleRange } from '@thive/fddm-types';

type Props = {
  ranges: VehicleRange[];
  inDetail?: boolean;
};

/**
 * Displays number or range of vehicles used in simulation
 */
export const VehicleCount = ({ ranges, inDetail = false }: Props) => {
  const { t } = useTranslation('interface');
  const messages = useMemo(() => {
    const vehiclesRangeTotal = ranges
      .filter((item) => item.range !== undefined)
      .map((item) => ({ least: item.range?.[0], most: item.range?.[item.range.length - 1] }))
      .reduce<{ least: number; most: number }>(
        (acc, vehicleAmount) => {
          acc.least = (acc.least ?? 0) + (vehicleAmount.least ?? 0);
          acc.most = (acc.most ?? 0) + (vehicleAmount.most ?? 0);

          return acc;
        },
        { least: 0, most: 0 },
      );

    const displayValue =
      vehiclesRangeTotal.least === vehiclesRangeTotal.most
        ? t(
            vehiclesRangeTotal.least === 1
              ? 'simulation.run_card.vehicle_count'
              : 'simulation.run_card.vehicles_count',
            { vehicleCount: vehiclesRangeTotal.least },
          )
        : t('simulation.run_card.vehicle_range', {
            from: vehiclesRangeTotal.least,
            to: vehiclesRangeTotal.most,
          });

    if (inDetail) {
      const displayValues = ranges.map(({ vehicleType, range }) => {
        const amountDisplayValue =
          range?.length === 1
            ? t(
                range[0] === 1
                  ? 'simulation.run_card.vehicle_count'
                  : 'simulation.run_card.vehicles_count',
                { vehicleCount: range[0] },
              )
            : t('simulation.run_card.vehicle_range', {
                from: vehiclesRangeTotal.least,
                to: vehiclesRangeTotal.most,
              });

        return `${vehicleType}: ${amountDisplayValue}`;
      });

      return displayValues;
    }

    return [displayValue];
  }, [ranges, t, inDetail]);

  return (
    <Stack>
      {messages.map((message) => (
        <Typography key={message} variant='caption' sx={{ fontSize: '10px' }}>
          {message}
        </Typography>
      ))}
    </Stack>
  );
};
